import React from 'react';
import { useTranslation } from 'react-i18next';

// Renders a legal document (terms or privacy) straight from the i18n catalog,
// walking whatever sectionN keys exist. The prerender in scripts/generate-static.ts
// mirrors this exactly, so the static HTML and the client render never drift and
// adding or renumbering a section is a catalog-only change.

type Section = Record<string, unknown>;

const List: React.FC<{ obj: Record<string, string>; indent: string }> = ({ obj, indent }) => (
  <ul className={`list-disc list-inside ${indent}`}>
    {Object.values(obj).map((v, i) => <li key={i}>{v}</li>)}
  </ul>
);

const LegalSection: React.FC<{ sec: Section }> = ({ sec }) => {
  const bodyKeys = Object.keys(sec)
    .filter((k) => /^body\d+$/.test(k))
    .sort((a, b) => Number(a.replace('body', '')) - Number(b.replace('body', '')));
  return (
    <section>
      <h2 className="text-2xl font-semibold mb-2">{sec.heading as string}</h2>
      {sec.body ? <p>{sec.body as string}</p> : null}
      {bodyKeys.map((k) => <p key={k} className="mb-2">{sec[k] as string}</p>)}
      {sec.intro ? <p>{sec.intro as string}</p> : null}
      {sec.items ? <List obj={sec.items as Record<string, string>} indent="ml-4" /> : null}
      {sec.subItems ? <List obj={sec.subItems as Record<string, string>} indent="ml-8" /> : null}
      {sec.dataProtectionHeading ? <p className="font-semibold mt-2">{sec.dataProtectionHeading as string}</p> : null}
      {sec.protectionItems ? <List obj={sec.protectionItems as Record<string, string>} indent="ml-4" /> : null}
      {sec.email ? (
        <p>
          <a href={`mailto:${sec.email as string}`} className="text-blue-600 hover:underline">{sec.email as string}</a>
        </p>
      ) : null}
    </section>
  );
};

const LegalDocument: React.FC<{ kind: 'terms' | 'privacy' }> = ({ kind }) => {
  const { t } = useTranslation();
  const doc = t(`legal.${kind}`, { returnObjects: true }) as Record<string, unknown>;
  const sectionKeys = Object.keys(doc)
    .filter((k) => /^section\d+$/.test(k))
    .sort((a, b) => Number(a.replace('section', '')) - Number(b.replace('section', '')));

  return (
    <div className="container max-w-3xl mx-auto px-4">
      <div className="space-y-8">
        <div className="flex flex-col items-center space-y-4">
          <img src="/currents-logo.png" alt="Currents Logo" className="w-16 h-16 rounded-2xl shadow-lg" />
          <h1 className="text-3xl font-black tracking-tight">{doc.title as string}</h1>
          <p className="text-sm text-gray-600">{doc.lastUpdated as string}</p>
          {doc.translationNotice ? (
            <p className="text-xs text-gray-500 text-center max-w-md">{doc.translationNotice as string}</p>
          ) : null}
        </div>
        {sectionKeys.map((k) => <LegalSection key={k} sec={doc[k] as Section} />)}
      </div>
    </div>
  );
};

export default LegalDocument;
